import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import { IoWarning, IoCheckmarkCircle, IoCloseCircle, IoRefresh, IoPlay, IoEye } from 'react-icons/io5';
import InventoryMatrix from './components/InventoryMatrix';
import CompareMatrix from './components/CompareMatrix';

interface Migration {
  name: string;
  file: string;
  status: 'pending' | 'applied' | 'failed' | 'rolled_back';
  description: string;
  appliedAt?: string;
  appliedBy?: string;
  duration?: number;
  isReversible: boolean;
  isDestructive: boolean;
  hasRollbackSql: boolean;
  riskScore: number;
  warnings: string[];
  createsTables: number;
  createsIndexes: number;
  altersTables: number;
}

interface MigrationData {
  migrations: {
    all: Migration[];
    pending: Migration[];
    applied: Migration[];
    failed: Migration[];
  };
  summary: {
    total: number;
    pending: number;
    applied: number;
    failed: number;
    lastRun: string | null;
  };
}

const MigrationManager: React.FC = () => {
  const [selectedDatabase, setSelectedDatabase] = useState<'dev' | 'prod'>('dev');
  const [selectedMigration, setSelectedMigration] = useState<Migration | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [activeTab, setActiveTab] = useState<'migrations' | 'inventory' | 'compare'>('migrations');
  const queryClient = useQueryClient();

  const { data, isLoading, refetch } = useQuery<MigrationData>(
    ['migrations', selectedDatabase],
    async () => {
      const response = await axios.get(`/api/v1/admin/migrations?target=${selectedDatabase}`);
      return response.data;
    },
    {
      refetchInterval: false,         // Disable auto-refresh
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false
    }
  );

  const applyMutation = useMutation(
    async ({ name, dryRun }: { name: string; dryRun: boolean }) => {
      const response = await axios.post(`/api/v1/admin/migrations/${name}/apply`, {
        dryRun,
        target: selectedDatabase
      });
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('migrations');
      }
    }
  );

  if (isLoading) {
    return <div className="p-6">Loading migrations...</div>;
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-800">Database Migrations</h1>
        <div className="flex gap-2">
          <div className="flex border border-gray-300 rounded overflow-hidden">
            <button
              onClick={() => setActiveTab('migrations')}
              className={`px-4 py-2 text-sm ${
                activeTab === 'migrations'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Migrations
            </button>
            <button
              onClick={() => setActiveTab('inventory')}
              className={`px-4 py-2 text-sm ${
                activeTab === 'inventory'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Inventory Matrix
            </button>
            <button
              onClick={() => setActiveTab('compare')}
              className={`px-4 py-2 text-sm ${
                activeTab === 'compare'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Dev vs Prod
            </button>
          </div>
          <button
            onClick={() => refetch()}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-2"
          >
            <IoRefresh size={16} />
            Refresh
          </button>
        </div>
      </div>

      {/* Show content based on active tab */}
      {activeTab === 'inventory' ? (
        <InventoryMatrix />
      ) : activeTab === 'compare' ? (
        <CompareMatrix />
      ) : (
        <>
          {/* Database Selector with WARNING */}
          <div className={`p-6 rounded-lg border-2 ${
        selectedDatabase === 'prod'
          ? 'bg-red-50 border-red-300'
          : 'bg-blue-50 border-blue-300'
      }`}>
        <div className="flex items-center gap-4">
          {selectedDatabase === 'prod' && (
            <div className="flex items-center gap-2 text-red-700">
              <IoWarning size={24} />
              <span className="font-bold text-lg">PRODUCTION DATABASE</span>
            </div>
          )}
          
          <div className="flex items-center gap-3">
            <span className="font-medium text-gray-700">Target Database:</span>
            <select
              value={selectedDatabase}
              onChange={(e) => setSelectedDatabase(e.target.value as 'dev' | 'prod')}
              className={`border-2 rounded px-4 py-2 font-medium ${
                selectedDatabase === 'prod'
                  ? 'border-red-500 bg-red-100 text-red-900'
                  : 'border-blue-500 bg-white'
              }`}
            >
              <option value="dev">Development Database</option>
              <option value="prod">⚠️ Production Database (DANGER)</option>
            </select>
          </div>

          {selectedDatabase === 'prod' && (
            <div className="flex-1 text-right">
              <span className="text-sm text-red-700 font-medium">
                ⚠️ Changes will affect PRODUCTION!
              </span>
            </div>
          )}
        </div>

        {selectedDatabase === 'prod' && (
          <div className="mt-4 p-4 bg-red-100 border border-red-300 rounded">
            <p className="text-sm text-red-800">
              <strong>WARNING:</strong> You are targeting the PRODUCTION database.
              All migrations will be applied to live customer data.
            </p>
          </div>
        )}
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-4 gap-4">
        <div className="p-4 bg-white rounded-lg shadow border-l-4 border-green-500">
          <div className="text-sm text-gray-600">Applied</div>
          <div className="text-3xl font-bold text-green-600">{data?.summary.applied || 0}</div>
        </div>
        <div className="p-4 bg-white rounded-lg shadow border-l-4 border-yellow-500">
          <div className="text-sm text-gray-600">Pending</div>
          <div className="text-3xl font-bold text-yellow-600">{data?.summary.pending || 0}</div>
        </div>
        <div className="p-4 bg-white rounded-lg shadow border-l-4 border-red-500">
          <div className="text-sm text-gray-600">Failed</div>
          <div className="text-3xl font-bold text-red-600">{data?.summary.failed || 0}</div>
        </div>
        <div className="p-4 bg-white rounded-lg shadow border-l-4 border-blue-500">
          <div className="text-sm text-gray-600">Last Run</div>
          <div className="text-sm font-medium text-gray-800">
            {data?.summary.lastRun ? new Date(data.summary.lastRun).toLocaleString() : 'Never'}
          </div>
        </div>
      </div>

      {/* Pending Migrations */}
      {data?.migrations.pending && data.migrations.pending.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">Pending Migrations ({data.migrations.pending.length})</h2>
          <div className="space-y-3">
            {data.migrations.pending.map((migration) => (
              <MigrationCard
                key={migration.name}
                migration={migration}
                onApply={() => applyMutation.mutate({ name: migration.name, dryRun: false })}
                onPreview={() => {
                  setSelectedMigration(migration);
                  setShowPreview(true);
                }}
              />
            ))}
          </div>
        </div>
      )}

      {/* Applied Migrations */}
      {data?.migrations.applied && data.migrations.applied.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">Applied Migrations ({data.migrations.applied.length})</h2>
          <div className="space-y-3">
            {data.migrations.applied.slice(0, 10).map((migration) => (
              <MigrationCard
                key={migration.name}
                migration={migration}
                onPreview={() => {
                  setSelectedMigration(migration);
                  setShowPreview(true);
                }}
              />
            ))}
          </div>
        </div>
      )}
        </>
      )}
    </div>
  );
};

const MigrationCard: React.FC<{
  migration: Migration;
  onApply?: () => void;
  onRollback?: () => void;
  onPreview?: () => void;
}> = ({ migration, onApply, onRollback, onPreview }) => {
  const getRiskColor = (score: number) => {
    if (score < 30) return { bg: 'bg-green-50', border: 'border-green-300', text: 'text-green-700', label: 'Low' };
    if (score < 60) return { bg: 'bg-yellow-50', border: 'border-yellow-300', text: 'text-yellow-700', label: 'Medium' };
    return { bg: 'bg-red-50', border: 'border-red-300', text: 'text-red-700', label: 'High' };
  };

  const risk = getRiskColor(migration.riskScore);

  return (
    <div className={`border rounded-lg p-4 ${
      migration.isDestructive ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white'
    }`}>
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-semibold text-gray-900">{migration.name}</h3>
            <StatusBadge status={migration.status} />
            {migration.isDestructive && (
              <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded font-medium">
                DESTRUCTIVE
              </span>
            )}
          </div>
          <p className="text-sm text-gray-600">{migration.description}</p>
        </div>
        
        <div className={`px-3 py-1 ${risk.bg} ${risk.border} border rounded text-sm font-medium ${risk.text}`}>
          Risk: {risk.label}
        </div>
      </div>

      {migration.warnings && migration.warnings.length > 0 && (
        <div className="mb-3 p-2 bg-yellow-50 border border-yellow-300 rounded text-sm text-yellow-800">
          {migration.warnings.map((w, i) => <div key={i}>⚠️ {w}</div>)}
        </div>
      )}

      <div className="flex gap-2">
        {migration.status === 'pending' && onApply && (
          <button
            onClick={onApply}
            className="px-3 py-1.5 bg-green-600 text-white text-sm rounded hover:bg-green-700 flex items-center gap-1"
          >
            <IoPlay size={14} />
            Apply
          </button>
        )}
        
        {onPreview && (
          <button
            onClick={onPreview}
            className="px-3 py-1.5 bg-gray-600 text-white text-sm rounded hover:bg-gray-700 flex items-center gap-1"
          >
            <IoEye size={14} />
            Preview
          </button>
        )}
      </div>

      {migration.status === 'applied' && migration.appliedAt && (
        <div className="mt-3 pt-3 border-t border-gray-200 text-xs text-gray-600">
          Applied {new Date(migration.appliedAt).toLocaleString()} by {migration.appliedBy}
          {migration.duration && ` • ${migration.duration}ms`}
        </div>
      )}
    </div>
  );
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const styles = {
    pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: '🟡' },
    applied: { bg: 'bg-green-100', text: 'text-green-800', icon: '✅' },
    failed: { bg: 'bg-red-100', text: 'text-red-800', icon: '❌' },
    rolled_back: { bg: 'bg-orange-100', text: 'text-orange-800', icon: '🔄' },
    running: { bg: 'bg-blue-100', text: 'text-blue-800', icon: '⚡' }
  };

  const style = styles[status] || styles.pending;

  return (
    <span className={`px-2 py-1 ${style.bg} ${style.text} text-xs rounded font-medium`}>
      {style.icon} {status.toUpperCase()}
    </span>
  );
};

export default MigrationManager;

